import React, { useCallback } from 'react'
import {
  StyleProp,
  StyleSheet,
  TouchableOpacity,
  TouchableOpacityProps,
  View,
  ViewStyle,
} from 'react-native'
import { useTheme } from '@react-navigation/native'
import { Theme } from '@theme'
import { IconName, Icon, Text } from '../atoms'

export type ButtonProps = TouchableOpacityProps & {
  title: string
  unfilled?: boolean
  isIconRight?: boolean
  iconName?: IconName
  iconColor?: string
  isGreen?: boolean
  isSmall?: boolean
  isBold?: boolean
  isGold?: boolean
  isRed?: boolean
  textColor?: string
  flex?: boolean
  hasPaddingHorizontal?: boolean
  borderWhite?: boolean
  borderWidth?: number
  customHeight?: number
  containerStyle?: StyleProp<ViewStyle>
  onPress: () => void
}

export const Button = ({
  title,
  unfilled = false,
  isIconRight = false,
  iconName,
  iconColor,
  isGreen = false,
  isSmall = false,
  isBold = false,
  isGold = false,
  isRed = false,
  textColor,
  flex,
  hasPaddingHorizontal = false,
  borderWhite = false,
  borderWidth,
  customHeight,
  containerStyle,
  disabled,
  onPress,
  ...props
}: ButtonProps): React.ReactElement => {
  const theme = useTheme()

  const styles = getStyle({ theme, unfilled, borderWhite, borderWidth, isSmall, isRed, customHeight, flex })

  const getColorByType = useCallback(
    (isText?: boolean) => {
      if (isText && !unfilled) return theme.colors.white

      if (isGreen) return theme.colors.green

      if (isGold) return theme.colors.gold

      return theme.colors.primary
    },
    [isGold, isGreen, unfilled, theme],
  )

  return (
    <View style={[styles.container, containerStyle, hasPaddingHorizontal && {paddingHorizontal: theme.spacing.lg}, disabled && {opacity: 0.5}]}>
      <TouchableOpacity
        disabled={disabled}
        onPress={onPress}
        style={[
          styles.button,
          isIconRight && {flexDirection: 'row-reverse'},
          { borderColor: borderWhite ? theme.colors.white : getColorByType() },
          props.style,
        ]}
      >
        {iconName && (
          <Icon
            name={iconName}
            color={iconColor}
            size={theme.iconSize.small}
          />
        )}
        <Text
          type={isSmall ? 'bodyExtraSmall' : 'body'}
          style={{
            ...styles.text,
            ...(isBold && styles.textBold),
          }}
          color={textColor || getColorByType(true)}
        >
          {title}
        </Text>
      </TouchableOpacity>
    </View>
  )
}

const getStyle = ({
  theme,
  unfilled,
  borderWhite,
  borderWidth,
  isSmall,
  isRed,
  customHeight,
  flex
}: {
  theme: Theme
  unfilled: boolean
  borderWhite: boolean
  borderWidth?: number
  isSmall: boolean
  isRed: boolean
  customHeight?: number
  flex?: boolean
}) =>
  StyleSheet.create({
    container: { width: isSmall ? undefined : '100%', flex: flex? 1 : undefined },
    button: {
      flexDirection: 'row',
      justifyContent: 'center',
      alignItems: 'center',

      gap: isSmall ? theme.spacing.sm : theme.spacing.lg,
      backgroundColor: isRed
        ? theme.colors.error
        : unfilled
          ? theme.colors.white
          : theme.colors.primary,
      paddingVertical: isSmall ? theme.spacing.xs * 2 : theme.spacing.md,
      paddingHorizontal: isSmall ? theme.spacing.lg : theme.spacing.md,
      borderRadius: theme.border.jumbo,
      borderWidth: isSmall? 1 : (borderWidth || (borderWhite || unfilled ? 2 : 0)),
      height: isSmall? undefined : customHeight || 50,
    },
    text: {
      textAlign: 'center',
    },
    textBold: {
      fontWeight: 'bold',
    },
    image: {
      width: theme.spacing.xl,
      height: theme.spacing.xl,
      marginRight: theme.spacing.lg,
    },
  })
